import { useFormContext } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useTrackEvent } from '@zep/logger/react';
import { useGetOwnedClassrooms } from '@/api/gen/classroom/classroom.gen';
import { useLocale } from '@/hooks';
import { ResetIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { AnimatedAvatar } from '@/shared/features/user-avatar';
import { selectData } from '@/shared/utils';
import { Button, IconButton } from '@/ui_v3';
import { type AvatarEventSource } from '../events.quiz-profile-avatar';
import { type AvatarDecorateForm } from '../schema';
import { MyPointsChip } from './MyPointsChip';
import { RandomAvatarButton } from './RandomAvatarButton';
const TEACHER_GRANT_BANNER_IMAGES = {
  ko: '/assets/avatar-decorate-jelly-banner-ko@2x.webp',
  en: '/assets/avatar-decorate-jelly-banner-en@2x.webp',
  th: '/assets/avatar-decorate-jelly-banner-th@2x.webp',
  id: '/assets/avatar-decorate-jelly-banner-id@2x.webp',
  ja: '/assets/avatar-decorate-jelly-banner-ja@2x.webp'
} as const;
type TeacherGrantBannerLocale = keyof typeof TEACHER_GRANT_BANNER_IMAGES;

/**
 * 우측 프리뷰 패널 (Figma node 1342:31476 우측 섹션).
 * 상단: 랜덤 꾸미기 + 저장된 상태로 되돌리기 + 코인 잔액(학생만) / 중앙: 전신 아바타 /
 * 하단: 저장.
 */
export const AvatarPreviewPanel = ({
  onSave,
  isSaving,
  onResetToSaved,
  source
}: {
  onSave: () => void;
  isSaving?: boolean;
  onResetToSaved: () => void;
  source: AvatarEventSource;
}) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale
  } = useLocale();
  const {
    watch,
    formState
  } = useFormContext<AvatarDecorateForm>();
  const {
    user
  } = useUser();
  const {
    track
  } = useTrackEvent();
  const avatar = watch('avatar');
  const shouldShowTeacherGrantBanner = !!user && user.type?.toLowerCase() !== 'student';
  const {
    data: ownedClassrooms = []
  } = useGetOwnedClassrooms(undefined, {
    query: {
      enabled: shouldShowTeacherGrantBanner,
      select: selectData,
      // 배너 링크 목적지(단일 클래스면 그 학생 목록, 아니면 전체 목록)만 정하는
      // 보조 조회다. 전역 기본값 throwOnError: true 를 그대로 두면 일시적인 500 이
      // 모달 안의 선택·저장까지 전역 오류 화면으로 날린다. 실패하면 기본 목록 링크로 둔다.
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const grantBannerHref = ownedClassrooms.length === 1 ? CLASSROOM_ROUTES.studentList(ownedClassrooms[0].classGroupId) : CLASSROOM_ROUTES.list();
  const language = locale.split('-')[0];
  const grantBannerImage = TEACHER_GRANT_BANNER_IMAGES[language as TeacherGrantBannerLocale] ?? TEACHER_GRANT_BANNER_IMAGES.en;
  return <div className="flex h-full flex-col bg-neutral" data-sentry-component="AvatarPreviewPanel" data-sentry-source-file="AvatarPreviewPanel.tsx">
      <div className="flex shrink-0 items-center justify-between bg-neutral px-md pb-none pt-md">
        <div className="flex gap-xs">
          <IconButton variant="outlined" intent="neutral" size="md" rounded="default" disabled={!formState.isDirty} aria-label={t('avatarDecorate.restoreSavedAriaLabel')} onClick={onResetToSaved} data-sentry-element="IconButton" data-sentry-source-file="AvatarPreviewPanel.tsx">
            <ResetIcon size="sm" color={formState.isDirty ? ICON_COLOR.neutral : ICON_COLOR.disabled} data-sentry-element="ResetIcon" data-sentry-source-file="AvatarPreviewPanel.tsx" />
          </IconButton>
          <RandomAvatarButton iconOnly data-sentry-element="RandomAvatarButton" data-sentry-source-file="AvatarPreviewPanel.tsx" />
        </div>
        <MyPointsChip data-sentry-element="MyPointsChip" data-sentry-source-file="AvatarPreviewPanel.tsx" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center px-none py-none">
        <div className="flex min-h-0 flex-1 items-center justify-center self-stretch">
          <div className="flex h-[128px] w-[96px] shrink-0 items-center justify-center">
            <AnimatedAvatar avatarData={avatar} behavior="stand" className="origin-center scale-[2]" data-sentry-element="AnimatedAvatar" data-sentry-source-file="AvatarPreviewPanel.tsx" />
          </div>
        </div>
        {shouldShowTeacherGrantBanner && <Link href={grantBannerHref} prefetch={false} aria-label={t('avatarDecorate.grantPointBanner')} onClick={() => track('quiz_profile_point_grant_banner_click', {
        source,
        destination: ownedClassrooms.length === 1 ? 'student_list' : 'classroom_list',
        ...(ownedClassrooms.length === 1 && {
          classroom_id: ownedClassrooms[0].classGroupId
        })
      })} className="group relative mt-[19px] aspect-[268/88] w-full max-w-[268px] shrink-0 transition-[filter,transform] duration-150 hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 motion-reduce:transition-none">
            <Image src={grantBannerImage} width={268} height={107} alt=""
        // 이미 Figma 에서 2x webp(q90)로 뽑은 정적 에셋이라 next/image 재최적화
        // (q75 재인코딩 + 사이즈 버킷 다운스케일)는 화질만 떨어뜨린다 → 원본 그대로 서빙.
        unoptimized className="absolute left-0 top-[-19px] h-[107px] w-full" priority={false} />
            <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[16px] [clip-path:inset(0_round_16px)]">
              <span className="absolute inset-0 animate-avatar-banner-shine bg-[linear-gradient(105deg,transparent_35%,rgba(255,255,255,0.18)_44%,rgba(255,255,255,0.58)_50%,rgba(255,255,255,0.18)_56%,transparent_65%)] bg-[length:200%_100%] bg-no-repeat motion-reduce:hidden" />
            </span>
          </Link>}
      </div>

      <div className="flex shrink-0 flex-col gap-sm px-md pb-lg pt-sm">
        <Button variant="contained" intent="strong" size="md" className="w-full" disabled={!formState.isDirty || isSaving} onClick={onSave} data-sentry-element="Button" data-sentry-source-file="AvatarPreviewPanel.tsx">
          {t('avatarDecorate.save')}
        </Button>
      </div>
    </div>;
};